import { Link } from "react-router-dom";
import { ArrowRight, Sparkle } from "@phosphor-icons/react";
import { Reveal } from "@/components/marketing/Motion";
import { ContourBackground } from "@/components/marketing/SresVisuals";

/* ------------------------------------------------------------------ *
 * Shared marketing atoms — used across every public page. Keep them  *
 * consistent so the site feels pixel-perfect end-to-end.             *
 *                                                                     *
 * All entrance motion routes through `<Reveal>` / `<StaggerGroup>` so *
 * every page shares one animation vocabulary. Subtle, professional,  *
 * once-only on scroll.                                                *
 * ------------------------------------------------------------------ */

export function Section({ children, className = "", id, testid }) {
  return (
    <section id={id} className={`w-full ${className}`} data-testid={testid}>
      <div className="max-w-7xl mx-auto px-6 md:px-10">{children}</div>
    </section>
  );
}

export function EyebrowLabel({ children, className = "" }) {
  return (
    <div
      className={`inline-flex items-center gap-2.5 text-[11px] font-semibold uppercase tracking-[0.28em] text-slate-500 ${className}`}
    >
      <span className="w-6 h-px bg-brand-blue" /> {children}
    </div>
  );
}

export function PageHero({ eyebrow, title, description, testid }) {
  return (
    <section
      className="w-full relative overflow-hidden border-b border-slate-100 bg-white"
      data-testid={testid}
    >
      {/* Very subtle grid + contour backdrop */}
      <div className="absolute inset-0 grid-lines opacity-40 pointer-events-none" aria-hidden />
      <div className="absolute -right-40 -top-32 w-[680px] h-[600px] opacity-[0.55] pointer-events-none" aria-hidden>
        <ContourBackground />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 md:px-10 pt-24 pb-20 md:pt-32 md:pb-24">
        <div className="max-w-3xl">
          {eyebrow ? (
            <Reveal variant="small">
              <div className="inline-flex items-center gap-2.5 text-[11px] font-semibold uppercase tracking-[0.28em] text-slate-500 mb-8">
                <span className="w-6 h-px bg-brand-blue" />
                {eyebrow}
              </div>
            </Reveal>
          ) : null}
          <Reveal variant="hero" delay={0.05}>
            <h1
              className="font-heading font-black text-slate-900 text-4xl sm:text-5xl lg:text-[62px] leading-[1.05] tracking-[-0.02em]"
              style={{ fontFamily: "'Fraunces', 'Chivo', ui-serif, serif" }}
            >
              {title}
            </h1>
          </Reveal>
          {description ? (
            <Reveal delay={0.15}>
              <p className="mt-8 text-slate-600 text-[17px] leading-[1.7] max-w-2xl">
                {description}
              </p>
            </Reveal>
          ) : null}
        </div>
      </div>
    </section>
  );
}

export function SectionHeading({ eyebrow, title, description, align = "left" }) {
  const alignCls = align === "center" ? "text-center mx-auto" : "text-left";
  return (
    <div className={`max-w-3xl ${alignCls}`}>
      {eyebrow ? (
        <Reveal variant="small">
          <EyebrowLabel className="mb-5">{eyebrow}</EyebrowLabel>
        </Reveal>
      ) : null}
      <Reveal delay={0.05}>
        <h2 className="font-heading font-black text-slate-900 text-3xl md:text-4xl lg:text-[44px] leading-[1.08] tracking-[-0.02em]">
          {title}
        </h2>
      </Reveal>
      {description ? (
        <Reveal delay={0.15}>
          <p className="mt-6 text-slate-600 text-[15.5px] leading-[1.7]">{description}</p>
        </Reveal>
      ) : null}
    </div>
  );
}

export function FeatureCard({ icon: Icon, title, description, testid }) {
  return (
    <Reveal
      className="group p-7 rounded-xl border border-slate-200 bg-white hover:border-slate-900 transition-colors duration-300"
      data-testid={testid}
    >
      {Icon ? (
        <div className="w-10 h-10 flex items-center justify-center border border-slate-200 rounded-lg mb-6 group-hover:border-slate-900 transition-colors">
          <Icon size={19} weight="regular" className="text-slate-700" />
        </div>
      ) : null}
      <div className="font-heading font-bold text-slate-900 text-[17px] leading-snug tracking-tight">{title}</div>
      <p className="mt-3 text-slate-600 text-[14px] leading-[1.7]">{description}</p>
    </Reveal>
  );
}

export function CalloutCard({ eyebrow, title, description, cta, to, testid }) {
  return (
    <Reveal>
      <div
        className="relative overflow-hidden rounded-2xl bg-[#0A0F1C] text-white p-10 md:p-14 border border-slate-900"
        data-testid={testid}
      >
        {/* Fine grid overlay for depth */}
        <div
          className="absolute inset-0 opacity-[0.05] pointer-events-none"
          style={{
            backgroundImage: "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)",
            backgroundSize: "60px 60px",
          }}
          aria-hidden
        />
        {/* Left accent stripe */}
        <div className="absolute left-0 top-10 bottom-10 w-px bg-brand-blue/60" aria-hidden />
        <div className="relative">
          {eyebrow ? (
            <div className="inline-flex items-center gap-2.5 text-[10.5px] font-semibold uppercase tracking-[0.32em] text-white/50">
              <span className="w-6 h-px bg-brand-blueLight" /> {eyebrow}
            </div>
          ) : null}
          <h3 className="mt-6 font-heading font-black text-3xl md:text-[38px] leading-[1.1] tracking-[-0.02em] max-w-2xl">
            {title}
          </h3>
          {description ? <p className="mt-5 text-white/70 max-w-2xl text-[15px] leading-[1.7]">{description}</p> : null}
          {cta && to ? (
            <Link
              to={to}
              className="mt-9 inline-flex items-center gap-2 h-12 px-6 rounded-md bg-white text-slate-900 text-[13.5px] font-semibold hover:bg-slate-100 transition-colors group"
              data-testid={`${testid || "callout"}-cta`}
            >
              {cta}
              <ArrowRight size={14} weight="bold" className="transition-transform group-hover:translate-x-0.5" />
            </Link>
          ) : null}
        </div>
      </div>
    </Reveal>
  );
}

export function StatTile({ value, label, testid }) {
  return (
    <Reveal
      className="p-6 rounded-xl border border-slate-200 bg-white hover:border-slate-900 transition-colors"
      data-testid={testid}
    >
      <div className="font-heading font-black text-3xl md:text-4xl text-slate-900 tracking-[-0.02em]">{value}</div>
      <div className="mt-3 text-[11px] uppercase tracking-[0.24em] text-slate-500">{label}</div>
    </Reveal>
  );
}

export function BulletList({ items, testid }) {
  return (
    <ul className="space-y-3" data-testid={testid}>
      {items.map((t, i) => (
        <Reveal
          key={i}
          as="li"
          variant="small"
          delay={i * 0.05}
          className="flex items-start gap-3 text-slate-700 text-[15px] leading-relaxed"
        >
          <span className="mt-2 w-1.5 h-1.5 rounded-full bg-brand-blue shrink-0" />
          <span>{t}</span>
        </Reveal>
      ))}
    </ul>
  );
}
